import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Dialog } from "primereact/dialog";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import {
  fetchTournamentsAsync,
  deleteTournamentAsync,
  type TournamentData,
} from "@/api/tournaments";
import { withSkeleton } from "@/helpers/tableBody";

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_ROW_COUNT = 10;

// ─── Column body templates ────────────────────────────────────────────────────

const startDateBody = withSkeleton<TournamentData>(
  row => (row.startDate ? humanDateWithoutTime(row.startDate) : "—"),
  "8rem"
);

const endDateBody = withSkeleton<TournamentData>(
  row => (row.endDate ? humanDateWithoutTime(row.endDate) : "—"),
  "8rem"
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Tournaments() {
  useBreadcrumb({
    label: "Tournaments",
    route: AdminAppRoutes.routes.tournaments.route,
    display: "none",
    resetTrail: true,
  });

  const navigate = useNavigate();
  const { showToast } = useToast();

  const [tournaments, setTournaments] = useState<TournamentData[] | "loading">("loading");
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState<TournamentData | null>(null);
  const [filters, setFilters] = useState({
    global: { value: "" as string | null, matchMode: FilterMatchMode.CONTAINS },
  });

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const load = async () => {
      try {
        const data = await fetchTournamentsAsync();
        setTournaments(data);
      } catch (error) {
        console.error("Error fetching tournaments:", error);
        setTournaments([]);
      }
    };

    load();
  }, []);

  const isLoading = tournaments === "loading";
  const skeletonData = Array<TournamentData | null>(DEFAULT_ROW_COUNT).fill(null);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTournamentClick = (tournamentId: number | undefined) => {
    if (tournamentId == null) return;
    navigate(
      AdminAppRoutes.routes.tournaments.routes.id.route.replace(
        ":tournamentId",
        String(tournamentId)
      )
    );
  };

  const handleLeaderboardClick = (tournamentId: number | undefined, tournamentName: string) => {
    if (tournamentId == null) return;
    navigate(
      AdminAppRoutes.routes.tournaments.routes.leaderboard.route.replace(
        ":id",
        String(tournamentId)
      ),
      { state: { tournamentName } }
    );
  };

  const canViewLeaderboard = (tournament: TournamentData) =>
    Boolean(
      tournament.ongoing &&
      Array.isArray(tournament.audience) &&
      (tournament.audience?.length ?? 0) > 0
    );

  const openDeleteDialog = (tournament: TournamentData) => {
    setTournamentToDelete(tournament);
    setIsDeleteDialogVisible(true);
  };

  const closeDeleteDialog = () => {
    setTournamentToDelete(null);
    setIsDeleteDialogVisible(false);
  };

  const confirmDeleteTournament = async () => {
    if (!tournamentToDelete?.id) return;
    try {
      await deleteTournamentAsync(tournamentToDelete.id);
      setTournaments(prev =>
        prev === "loading" ? prev : prev.filter(t => t.id !== tournamentToDelete.id)
      );
      showToast({ severity: "success", detail: "Tournament deleted successfully" });
    } catch (error) {
      console.error("Error deleting tournament:", error);
      showToast({ severity: "error", detail: "Failed to delete tournament" });
    } finally {
      closeDeleteDialog();
    }
  };

  // ─── Column body templates (need component-level deps) ────────────────────

  const nameBody = (rowData: TournamentData | null) => {
    if (rowData == null) return <Skeleton width="12rem" />;
    return <Button label={rowData.name} link onClick={() => handleTournamentClick(rowData.id)} />;
  };

  const leaderboardBody = (rowData: TournamentData | null) => {
    if (rowData == null) return <Skeleton width="9rem" />;
    if (!canViewLeaderboard(rowData)) return null;
    return (
      <Button
        severity="secondary"
        label="View Leaderboard"
        icon="pi pi-chart-bar"
        data-testid={`tournament-view-leaderboard-${rowData.id}`}
        onClick={() => handleLeaderboardClick(rowData.id, rowData.name ?? "")}
      />
    );
  };

  const deleteBody = (rowData: TournamentData | null) => {
    if (rowData == null) return <Skeleton width="3rem" />;
    return (
      <Button
        icon="pi pi-trash"
        severity="danger"
        outlined
        onClick={() => openDeleteDialog(rowData)}
        aria-label="Delete tournament"
        data-testid={`tournament-delete-${rowData.id}`}
      />
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <h1>Tournaments</h1>
          <Button
            label="New Tournament"
            icon="pi pi-plus"
            data-testid="new-tournament-button"
            onClick={() => navigate(AdminAppRoutes.routes.tournaments.routes.new.route)}
          />
        </div>
      </div>

      <Card>
        <IconField className="mb-4" iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            placeholder="Search Tournaments"
            aria-label="Search tournaments"
            data-testid="tournaments-search-input"
            value={filters.global.value ?? ""}
            onChange={e =>
              setFilters({
                global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS },
              })
            }
          />
        </IconField>
        <DataTable
          paginator
          rows={DEFAULT_ROW_COUNT}
          aria-label="Tournaments list"
          data-testid="tournaments-table"
          value={
            (isLoading
              ? skeletonData
              : (tournaments as TournamentData[])) as unknown as DataTableValueArray
          }
          filters={filters}
          globalFilterFields={["name", "startDate", "endDate"]}
          emptyMessage="No tournaments found"
          tableClassName="dt-responsive-text"
        >
          <Column field="name" header="Tournament Name" sortable body={nameBody} />
          <Column field="startDate" header="Start Date" sortable body={startDateBody} />
          <Column field="endDate" header="End Date" sortable body={endDateBody} />
          <Column header="" body={leaderboardBody} />
          <Column header="" body={deleteBody} />
        </DataTable>
      </Card>

      <Dialog
        header="Confirm Deletion"
        visible={isDeleteDialogVisible}
        style={{ width: "400px" }}
        modal
        onHide={closeDeleteDialog}
        data-testid="tournament-delete-confirm-dialog"
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              outlined
              onClick={closeDeleteDialog}
              data-testid="tournament-delete-cancel-button"
            />
            <Button
              label="Delete"
              icon="pi pi-check"
              severity="danger"
              data-testid="tournament-delete-confirm-button"
              onClick={() => void confirmDeleteTournament()}
            />
          </>
        }
      >
        {tournamentToDelete && (
          <p>
            Are you sure you want to delete the tournament &quot;
            <strong>{tournamentToDelete.name}</strong>&quot;?
          </p>
        )}
      </Dialog>
    </>
  );
}
